// Cookiebot consent helper. Cookiebot loads via a <script> in nuxt.config head
// and emits global window events when the user accepts/declines categories.
// Plugins that fire trackers (PostHog, Sentry) wait on these instead of
// initialising on page load.
//
// Categories: necessary | preferences | statistics | marketing.
// Findex maps:
//   - PostHog (analytics + session replay) -> statistics
//   - Sentry  (error tracking + replay)    -> statistics
//   - GTM marketing tags                   -> marketing (handled inside GTM itself)

type CookiebotCategory = "necessary" | "preferences" | "statistics" | "marketing";

interface CookiebotGlobal {
  consent: Record<CookiebotCategory, boolean>;
  hasResponse: boolean;
  show: () => void;
  renew: () => void;
}

declare global {
  interface Window {
    Cookiebot?: CookiebotGlobal;
  }
}

const ready: Array<(c: CookiebotGlobal) => void> = [];
let initialised = false;

const dispatch = () => {
  if (typeof window === "undefined" || !window.Cookiebot) return;
  const c = window.Cookiebot;
  while (ready.length > 0) {
    const cb = ready.shift();
    cb?.(c);
  }
};

const init = () => {
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  window.addEventListener("CookiebotOnAccept", dispatch);
  window.addEventListener("CookiebotOnDecline", dispatch);
  window.addEventListener("CookiebotOnConsentReady", dispatch);

  // Cookiebot loads its uc.js asynchronously, so window.Cookiebot may not exist
  // on the first tick. Poll briefly, then either dispatch (consent already
  // cached) or explicitly show the banner — manual blocking mode does NOT
  // auto-display it.
  const trigger = () => {
    const c = window.Cookiebot;
    if (!c) return false;
    if (c.hasResponse) dispatch();
    else c.show();
    return true;
  };
  if (!trigger()) {
    const start = Date.now();
    const interval = window.setInterval(() => {
      if (trigger() || Date.now() - start > 5000) window.clearInterval(interval);
    }, 100);
  }
};

export const onConsent = (category: CookiebotCategory, granted: () => void) => {
  init();
  const handler = (c: CookiebotGlobal) => {
    if (c.consent[category]) granted();
  };
  if (typeof window !== "undefined" && window.Cookiebot?.hasResponse) {
    handler(window.Cookiebot);
    return;
  }
  ready.push(handler);
};

export const hasConsent = (category: CookiebotCategory): boolean => {
  if (typeof window === "undefined") return false;
  return Boolean(window.Cookiebot?.consent?.[category]);
};
